import { useState, useEffect } from 'react';
import { useAuth } from '@/context';
import { institutionUsers, type User } from '@/services/institution';
import { Button, Input, Modal, Skeleton } from '@/components/common';
import styles from './ProfilePage.module.css';

export function ProfilePage() {
  const { user: authUser } = useAuth();
  const [userDetails, setUserDetails] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Password change modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    async function fetchUserDetails() {
      if (!authUser?.id) return;

      setLoading(true);
      try {
        const response = await institutionUsers.getById(authUser.id);
        if (response.success && response.data) {
          setUserDetails(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch user details:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchUserDetails();
  }, [authUser?.id]);

  function openPasswordModal() {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setPasswordSuccess(false);
    setShowPasswordModal(true);
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setPasswordError(null);

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Please fill in all fields');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setChangingPassword(true);

    try {
      // Note: This would typically call a password change API endpoint
      // For now, we'll simulate a success
      await new Promise(resolve => setTimeout(resolve, 1000));
      setPasswordSuccess(true);
      setTimeout(() => {
        setShowPasswordModal(false);
      }, 2000);
    } catch (err) {
      setPasswordError('Failed to change password. Please try again.');
    } finally {
      setChangingPassword(false);
    }
  }

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>My Profile</h1>
        <p className={styles.subtitle}>View and manage your account information</p>
      </div>

      <div className={styles.content}>
        {/* Profile Card */}
        <div className={styles.profileCard}>
          {loading ? (
            <div className={styles.profileLoading}>
              <Skeleton variant="circular" width={100} height={100} />
              <div className={styles.profileLoadingInfo}>
                <Skeleton variant="text" width="60%" height={28} />
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="text" width="30%" />
              </div>
            </div>
          ) : (
            <>
              <div className={styles.profileHeader}>
                <div className={styles.avatar}>
                  {userDetails?.firstName?.[0] || authUser?.firstName?.[0] || 'U'}
                  {userDetails?.lastName?.[0] || authUser?.lastName?.[0] || ''}
                </div>
                <div className={styles.profileInfo}>
                  <h2 className={styles.userName}>
                    {userDetails?.firstName || authUser?.firstName} {userDetails?.lastName || authUser?.lastName}
                  </h2>
                  <span className={styles.userEmail}>{userDetails?.email || authUser?.email}</span>
                  {userDetails?.jobTitle && (
                    <span className={styles.userTitle}>{userDetails.jobTitle}</span>
                  )}
                </div>
                <div className={styles.statusBadge} data-active={userDetails?.isActive ?? true}>
                  {userDetails?.isActive ? 'Active' : 'Inactive'}
                </div>
              </div>

              <div className={styles.profileActions}>
                <Button variant="secondary" onClick={openPasswordModal}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  Change Password
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Information Sections */}
        <div className={styles.sections}>
          {/* Personal Information */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              Personal Information
            </h3>
            {loading ? (
              <div className={styles.loadingGrid}>
                <Skeleton variant="text" />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </div>
            ) : (
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Full Name</span>
                  <span className={styles.infoValue}>
                    {userDetails?.firstName} {userDetails?.lastName}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Email Address</span>
                  <span className={styles.infoValue}>{userDetails?.email}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Phone</span>
                  <span className={styles.infoValue}>{userDetails?.phone || 'Not provided'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Employee ID</span>
                  <span className={styles.infoValue}>{userDetails?.employeeId || 'Not assigned'}</span>
                </div>
              </div>
            )}
          </section>

          {/* Organization Information */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M7 17V7M7 7V3H13V7M7 7H3V17H17V7H13M13 7V17" />
              </svg>
              Organization
            </h3>
            {loading ? (
              <div className={styles.loadingGrid}>
                <Skeleton variant="text" />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </div>
            ) : (
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Department</span>
                  <span className={styles.infoValue}>
                    {userDetails?.department?.name || 'Not assigned'}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Job Title</span>
                  <span className={styles.infoValue}>{userDetails?.jobTitle || 'Not specified'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Security Clearance</span>
                  <span className={styles.infoValue}>Level {userDetails?.securityClearance || 1}</span>
                </div>
              </div>
            )}
          </section>

          {/* Roles & Permissions */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              Roles & Permissions
            </h3>
            {loading ? (
              <div className={styles.loadingRoles}>
                <Skeleton variant="rounded" width={80} height={28} />
                <Skeleton variant="rounded" width={100} height={28} />
              </div>
            ) : userDetails?.roles && userDetails.roles.length > 0 ? (
              <div className={styles.rolesList}>
                {userDetails.roles.map((role) => (
                  <span key={role.id} className={styles.roleBadge}>
                    {role.name}
                  </span>
                ))}
              </div>
            ) : (
              <p className={styles.noRoles}>No roles assigned</p>
            )}
          </section>

          {/* Account Information */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              Account Details
            </h3>
            {loading ? (
              <div className={styles.loadingGrid}>
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </div>
            ) : (
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Account Created</span>
                  <span className={styles.infoValue}>
                    {userDetails?.createdAt ? formatDate(userDetails.createdAt) : 'Unknown'}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Last Updated</span>
                  <span className={styles.infoValue}>
                    {userDetails?.updatedAt ? formatDate(userDetails.updatedAt) : 'Unknown'}
                  </span>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Change Password Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title="Change Password"
        size="sm"
      >
        {passwordSuccess ? (
          <div className={styles.successMessage}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <h3>Password Changed Successfully</h3>
            <p>Your password has been updated.</p>
          </div>
        ) : (
          <form onSubmit={handleChangePassword} className={styles.passwordForm}>
            {passwordError && (
              <div className={styles.error}>{passwordError}</div>
            )}

            <Input
              label="Current Password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter your current password"
              required
            />

            <Input
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter your new password"
              hint="Minimum 8 characters"
              required
            />

            <Input
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              required
            />

            <div className={styles.modalActions}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowPasswordModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={changingPassword}>
                {changingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
